import { z } from 'zod'

/**
 * Trust: earned autonomy, audits, error budgets and the learning loop (design 13), and the
 * shapes the console uses for them. Kept apart from config.ts so this file has no imports of
 * its own (config.ts imports the policy from here).
 */

// ---------------------------------------------------------------------------
// The company's rules for earning and losing autonomy (design 13.1, 13.4)
// ---------------------------------------------------------------------------
export const PromotionRule = z.object({
  /** Real cases with a known human outcome in the window. */
  min_samples: z.number().int().positive(),
  /** Share sent unchanged or nearly unchanged (at Level 0: share where people sent essentially the AI's answer). */
  min_rate: z.number().min(0).max(1),
})
export type PromotionRule = z.infer<typeof PromotionRule>

export const AutonomyPolicy = z.object({
  /** The evidence window, in days. */
  window_days: z.number().int().min(7).max(120).default(30),
  /** The bar for each step up: from Level 0 to 1, 1 to 2 and 2 to 3. */
  promote: z.object({
    '0': PromotionRule.default({ min_samples: 30, min_rate: 0.8 }),
    '1': PromotionRule.default({ min_samples: 50, min_rate: 0.9 }),
    '2': PromotionRule.default({ min_samples: 50, min_rate: 0.95 }),
  }).prefault({}),
  /** An edited reply this similar to the AI's draft (0 to 1) counts as "nearly unchanged". */
  near_unchanged: z.number().min(0.5).max(1).default(0.85),
  /** Below this similarity a reply counts as rewritten; between the two it was lightly edited. */
  rewritten_below: z.number().min(0).max(0.95).default(0.5),
  /** Evals must pass before a step up (the eval gate). */
  evals: z.object({
    required: z.boolean().default(true),
    min_cases: z.number().int().nonnegative().default(3),
    min_pass_rate: z.number().min(0).max(1).default(1),
  }).prefault({}),
  /** The audit sample must be correct before Level 3 (only Level 2 and 3 replies are audited). */
  audits: z.object({
    min_graded: z.number().int().nonnegative().default(10),
    min_correct_rate: z.number().min(0).max(1).default(0.95),
  }).prefault({}),
  /** Automatic demotion. */
  demote: z.object({
    min_samples: z.number().int().positive().default(30),
    below_rate: z.number().min(0).max(1).default(0.85),
    /** Complaints per customer message for the request type over the window. */
    complaint_rate: z.number().min(0).max(1).default(0.1),
    min_complaints: z.number().int().positive().default(3),
  }).prefault({}),
  /** Critical errors allowed per week for one request type before it drops to Level 1 (the company budget is error_budget_per_week). */
  error_budget_by_subject: z.record(z.string(), z.number().int().nonnegative()).default({}),
  /** Drop Level 3 to Level 2 when its playbook or knowledge changes without a passing re-test. */
  retest_on_change: z.boolean().default(true),
  /** Roll a release back on its own when it makes things worse. */
  auto_rollback: z.object({
    enabled: z.boolean().default(true),
    watch_days: z.number().int().min(1).max(60).default(14),
    /** How much worse the edit rate may get (absolute, 0 to 1) before the release is rolled back. */
    worse_by: z.number().min(0).max(1).default(0.15),
    min_samples: z.number().int().positive().default(10),
  }).prefault({}),
})
export type AutonomyPolicy = z.infer<typeof AutonomyPolicy>
export const DEFAULT_AUTONOMY_POLICY: AutonomyPolicy = AutonomyPolicy.parse({})

// ---------------------------------------------------------------------------
// Autonomy grid extras (screen 18.8)
// ---------------------------------------------------------------------------
export type AutonomyCheckKey = 'cases' | 'unchanged' | 'critical' | 'evals' | 'audits' | 'complaints' | 'retest' | 'budget'
export interface AutonomyCheckView {
  key: AutonomyCheckKey
  label: string
  value: string
  need: string
  /** null: not a test at this level (for example audits below Level 2). */
  ok: boolean | null
}
export interface AutonomyHistoryItem {
  id: string; at: string; kind: string; from_level: number; to_level: number; reason: string; status: string; by: string | null
}
export interface DraftOutcomeCounts { unchanged: number; light: number; rewritten: number }
export interface AutonomyCellTrust {
  veto_minutes?: number
  /** How the cell behaves, in the screen's words ("Veto 10 min", "Auto if sure, else hand over"). */
  behaviour?: string
  /** All the tests for the next step up, with the company's thresholds. */
  checks?: AutonomyCheckView[]
  /** Promotions, demotions and changes, newest first. */
  history?: AutonomyHistoryItem[]
  /** Set when a playbook or knowledge change has not been re-tested yet. */
  retest?: { since: string; reason: string } | null
  outcomes?: DraftOutcomeCounts
  window_days?: number
  /** False for a channel's own level: it can be removed, and the channel then uses "all channels" again. */
  removable?: boolean
}
export const AutonomySettingsInput = z.object({
  veto_minutes: z.number().int().min(1).max(24 * 60).optional(),
  max_level: z.number().int().min(0).max(3).optional(),
  locked_reason: z.string().max(300).nullish(),
  reason: z.string().min(1).max(1000),
})
export const AutonomyRemoveInput = z.object({ reason: z.string().min(1).max(1000) })
export const AutonomyRaiseInput = z.object({
  level: z.number().int().min(0).max(3), reason: z.string().min(1),
  /** Raise even though the evidence does not meet the bar (recorded, and always a second admin). */
  without_evidence: z.boolean().optional(),
})

export interface ErrorBudgetView {
  week: string
  per_week: number
  used: number
  subjects: { subject_key: string; label: string; used: number; budget: number | null }[]
}
export interface TrustOverview {
  policy: AutonomyPolicy
  error_budget: ErrorBudgetView
  audit_sample_rate: number
  /** Why no promotion is proposed right now (the company is over its error budget), or null. */
  promotions_paused: string | null
}

// ---------------------------------------------------------------------------
// Shadow-mode report: what people did with the AI's drafts, per week
// ---------------------------------------------------------------------------
export interface ShadowBucket {
  /** Drafts with a known human outcome. */
  drafts: number
  /** Sent as the AI wrote them (approved, not vetoed, or the person sent the same answer). */
  unchanged: number
  /** Changed a little: at least the company's "nearly unchanged" similarity. */
  light: number
  /** Changed more than that, but not rewritten. */
  edited: number
  /** Rewritten, rejected or taken over. */
  rewritten: number
  /** Rounds of editing before the reply went out. Auto-sent replies count as 0. */
  edits0: number
  edits1: number
  edits2plus: number
  /** Sent by the AI with no person involved (Level 2 and 3). */
  auto_sent: number
}
export interface ShadowStatsRow extends ShadowBucket {
  week: string
  request_type: string
  channel: string
  /** Sent unchanged or nearly, over drafts. */
  unchanged_rate: number | null
  /** Share "sent with at most one edit". */
  at_most_one_edit_rate: number | null
}
export interface ShadowStatsView {
  from: string
  to: string
  weeks: string[]
  rows: ShadowStatsRow[]
  /** One row per week across every request type and channel. */
  totals: ShadowStatsRow[]
}

// ---------------------------------------------------------------------------
// Improve and change-set tests (screens 18.9, 18.13)
// ---------------------------------------------------------------------------
export type TestVerdict = 'better' | 'same' | 'worse'
export interface TestCaseView {
  name: string; ok: boolean; detail: string
  /** From a label no person reviewed: shown, never blocks a release (only reviewed labels can). */
  advisory?: boolean
  suite?: string
  verdict?: TestVerdict
  input?: string
  before_output?: string
  after_output?: string
  /** What a person actually sent for that message (a replay of a real conversation). */
  human_output?: string
}
export interface TestSummaryView { better: number; same: number; worse: number; evals_passed: number; evals_total: number; replayed: number }
export interface InsightEffectView {
  measured_at: string
  released_at: string
  request_type: string | null
  /** Share of drafts people edited or rewrote, before and since the release. */
  edit_rate_before: number | null
  edit_rate_after: number | null
  samples_before: number
  samples_after: number
  touches_per_week_before: number
  touches_per_week_after: number
  escalations_per_week_before: number | null
  escalations_per_week_after: number | null
  verdict: 'better' | 'same' | 'worse' | 'too_early'
}
export interface InsightTrust {
  impact_band?: 'high' | 'medium' | 'low'
  /** What kind of fix: knowledge, playbook, task template, guardrail, question or autonomy. */
  fix_kind?: string | null
  effect?: InsightEffectView | null
  rolled_back?: { at: string; reason: string; automatic: boolean } | null
}
export interface ChangeSetTrust {
  /** When the creator asked a second admin to approve. */
  approval_requested_at?: string | null
  tested_at?: string | null
  /** Set once a release was rolled back: by a person, or automatically by the release watch. */
  rolled_back?: { at: string; by: string | null; reason: string | null; automatic: boolean } | null
  /** After release: what the watch measured (the request types it affects and their edit rate before and since). */
  watch?: { until: string; request_types: string[]; edit_rate_before: number | null; edit_rate_after: number | null; samples_after: number; verdict: string | null } | null
}
export interface InsightsSummary { week: string; touches: number; insights: number }

export const ChangeSetEditInput = z.object({
  title: z.string().min(1).max(200).optional(),
  /** The full list of changes ({entity, op, key, after, summary}); validated like a new draft. */
  changes: z.array(z.unknown()).min(1).max(50),
})

// ---------------------------------------------------------------------------
// Configuration as code (design 15.1 #8)
// ---------------------------------------------------------------------------
export interface ConfigExport {
  format: 'takeit4me.config'
  version: 1
  exported_at: string
  company: { name: string; slug: string; region: string; industry_pack: string | null }
  /** One file per entity: settings.json, brand.json, playbooks/<key>.json, questions/<key>.json ... */
  files: Record<string, unknown>
}
export const ConfigImportInput = z.object({
  title: z.string().min(1).max(200).default('Import configuration'),
  files: z.record(z.string(), z.unknown()),
})

// ---------------------------------------------------------------------------
// Customer ratings (design 13.5: an input to the learning loop)
// ---------------------------------------------------------------------------
export const RatingInput = z.object({ rating: z.number().int().min(1).max(5), comment: z.string().max(2000).optional() })
